import { getPublicAiModeLabel, type PublicAiMode } from "../shared/consumer-language";
import {
  listStarterApiInferenceProviders,
  type InferenceProviderFieldDefinition,
  type InferenceProviderId,
} from "../../../src/shared/inference-provider-registry.js";

export type StarterSubscriptionProviderId = "anthropic" | "openai-codex" | "google-gemini-cli";
export type StarterApiKeyProviderId = InferenceProviderId;
export type StarterAccountConnectionId =
  | "operator-email"
  | "operator-calendar"
  | "agent-email"
  | "agent-calendar";

export interface StarterSubscriptionProvider {
  id: string;
  label: string;
  description: string;
  flowType: "device_code" | "token_paste" | "redirect";
  catalogProvider: string;
  instructions?: string;
}

export interface StarterApiKeyProvider {
  id: StarterApiKeyProviderId;
  label: string;
  description: string;
  integrationId: StarterApiKeyProviderId;
  fields: StarterApiKeyField[];
  placeholder: string;
  helper: string;
}

export interface StarterApiKeyField {
  id: string;
  label: string;
  kind: InferenceProviderFieldDefinition["kind"];
  placeholder: string;
  helper: string;
  required: boolean;
  secret?: boolean;
}

export interface StarterAiChoice {
  id: PublicAiMode;
  label: string;
  description: string;
  helper: string;
}

export interface StarterAccountConnection {
  id: StarterAccountConnectionId;
  label: string;
  description: string;
  detail: string;
}

interface StarterSubscriptionDefinition extends StarterSubscriptionProvider {
  id: StarterSubscriptionProviderId;
}

const STARTER_AI_CHOICES: StarterAiChoice[] = [
  {
    id: "workspace_default",
    label: getPublicAiModeLabel("workspace_default"),
    description: "Nothing else to set up right now.",
    helper: "You can keep going now, or add your own subscription or API key.",
  },
  {
    id: "byo_subscription",
    label: getPublicAiModeLabel("byo_subscription"),
    description: "Use one of your connected AI subscriptions first.",
    helper: "Good if you already pay for a supported account and want that used first.",
  },
  {
    id: "byo_api",
    label: getPublicAiModeLabel("byo_api"),
    description: "Paste credentials for a supported AI provider.",
    helper: "Good if you want your own billing and more direct control.",
  },
];

const STARTER_SUBSCRIPTION_DEFINITIONS: StarterSubscriptionDefinition[] = [
  {
    id: "anthropic",
    label: "Claude",
    description: "Use your Claude subscription first.",
    flowType: "token_paste",
    catalogProvider: "anthropic",
    instructions: "Paste the Claude token for this connection, or skip this for now.",
  },
  {
    id: "openai-codex",
    label: "ChatGPT",
    description: "Use your ChatGPT subscription first.",
    flowType: "redirect",
    catalogProvider: "openai",
  },
  {
    id: "google-gemini-cli",
    label: "Gemini",
    description: "Use your Gemini subscription first.",
    flowType: "device_code",
    catalogProvider: "google",
  },
];

function toStarterApiKeyField(field: InferenceProviderFieldDefinition): StarterApiKeyField {
  return {
    id: field.id,
    label: field.label,
    kind: field.kind,
    placeholder: field.placeholder,
    helper: field.helper,
    required: field.required,
    secret: field.secret,
  };
}

const STARTER_API_KEY_DEFINITIONS: StarterApiKeyProvider[] = listStarterApiInferenceProviders().map(
  (provider) => {
    const primaryField = provider.fields.find((field) => field.id === "key") ?? provider.fields[0];
    return {
      id: provider.id,
      integrationId: provider.id,
      label: provider.label,
      description: provider.starterApi?.description ?? provider.description,
      fields: provider.fields.map(toStarterApiKeyField),
      placeholder: primaryField?.placeholder ?? "Paste credential",
      helper: provider.starterApi?.helper ?? primaryField?.helper ?? "Paste your credentials here.",
    };
  },
);

const STARTER_ACCOUNT_CONNECTIONS: StarterAccountConnection[] = [
  {
    id: "operator-email",
    label: "Your inbox",
    description: "Let your agent read your Gmail inbox so it can triage and draft safely.",
    detail: "Read-only by default.",
  },
  {
    id: "operator-calendar",
    label: "Your calendar",
    description: "Let your agent read your calendar so it can plan around your schedule.",
    detail: "Read-only by default.",
  },
  {
    id: "agent-email",
    label: "Agent email",
    description: "Give your agent a separate email identity for outbound email.",
    detail: "Optional. Separate from your personal inbox.",
  },
  {
    id: "agent-calendar",
    label: "Agent calendar",
    description: "Create a separate calendar your agent can manage directly.",
    detail: "Optional. Separate from your personal calendar.",
  },
];

const STARTER_SUBSCRIPTION_ORDER = new Map(
  STARTER_SUBSCRIPTION_DEFINITIONS.map((provider, index) => [provider.id, index]),
);

export function getStarterAiChoices(): StarterAiChoice[] {
  return STARTER_AI_CHOICES.map((choice) => ({ ...choice }));
}

export function getStarterSubscriptionChoices(): StarterSubscriptionProvider[] {
  return STARTER_SUBSCRIPTION_DEFINITIONS.map((provider) => ({ ...provider }));
}

export function getStarterVisibleSubscriptionProviders(
  providers: readonly StarterSubscriptionProvider[],
): StarterSubscriptionProvider[] {
  const knownProviders = new Map(
    providers
      .filter((provider) =>
        STARTER_SUBSCRIPTION_ORDER.has(provider.id as StarterSubscriptionProviderId),
      )
      .map((provider) => [provider.id, provider]),
  );

  return STARTER_SUBSCRIPTION_DEFINITIONS.map((definition) => {
    const liveProvider = knownProviders.get(definition.id);
    if (!liveProvider) return { ...definition };
    return {
      ...liveProvider,
      label: definition.label,
      description: definition.description,
      instructions: definition.instructions ?? liveProvider.instructions,
    };
  }).sort(
    (left, right) =>
      (STARTER_SUBSCRIPTION_ORDER.get(left.id as StarterSubscriptionProviderId) ??
        Number.MAX_SAFE_INTEGER) -
      (STARTER_SUBSCRIPTION_ORDER.get(right.id as StarterSubscriptionProviderId) ??
        Number.MAX_SAFE_INTEGER),
  );
}

export function getStarterApiKeyChoices(): StarterApiKeyProvider[] {
  return STARTER_API_KEY_DEFINITIONS.map((provider) => ({
    ...provider,
    fields: provider.fields.map((field) => ({ ...field })),
  }));
}

export function buildStarterApiIntegrationPayload(
  provider: StarterApiKeyProvider,
  values: Record<string, string>,
): Record<string, unknown> {
  const normalizedValues = Object.fromEntries(
    Object.entries(values)
      .map(([key, value]) => [key, value.trim()] as const)
      .filter(([, value]) => value.length > 0),
  );
  const payload: Record<string, unknown> = {};

  const keyValue = normalizedValues.key;
  if (keyValue) {
    payload.key = keyValue;
  }

  const metadataEntries = Object.entries(normalizedValues).filter(([fieldId]) => fieldId !== "key");
  if (metadataEntries.length > 0) {
    payload.metadata = Object.fromEntries(metadataEntries);
  }

  return payload;
}

export function hasCompleteStarterApiValues(
  provider: StarterApiKeyProvider,
  values: Record<string, string>,
): boolean {
  return provider.fields.every((field) => {
    if (!field.required) return true;
    return Boolean(values[field.id]?.trim());
  });
}

export function getStarterAccountConnections(): StarterAccountConnection[] {
  return STARTER_ACCOUNT_CONNECTIONS.map((connection) => ({ ...connection }));
}
